/**
 * Admin-only widget showing who has desktop push notifications enabled.
 * Shown inside Settings for super_admin / admin / ceo. Lets you quickly
 * see who to nudge so they don't miss leave approvals, chat pings, etc.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Users, CheckCircle2, XCircle, Search, RefreshCw, Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api';
import logger from '@/lib/logger';

const fmtRole = (r) => (r || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const UserRow = ({ user, tone }) => (
    <div className="flex items-center justify-between py-1.5 text-sm" data-testid={`push-stat-row-${user.id}`}>
        <span className="font-medium truncate">{user.full_name}</span>
        <Badge variant="outline" className={`text-[10px] ${tone === 'on' ? 'border-emerald-500/40 text-emerald-600' : 'border-muted-foreground/30'}`}>
            {fmtRole(user.role)}
        </Badge>
    </div>
);

export default function PushNotificationsStatusCard({ visible = true }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [tab, setTab] = useState('disabled'); // start on disabled — those are the ones to nudge

    const fetch = useCallback(async () => {
        try {
            setLoading(true);
            const res = await apiClient.get('/push/stats');
            setStats(res.data);
        } catch (e) {
            logger.error('push stats failed', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (visible) fetch();
    }, [fetch, visible]);

    if (!visible) return null;

    const users = stats ? (tab === 'enabled' ? stats.enabled_users : stats.disabled_users) : [];
    const s = (search || '').toLowerCase();
    const filtered = s ? users.filter((u) => (u.full_name || '').toLowerCase().includes(s)) : users;

    return (
        <Card data-testid="push-stats-card">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        <CardTitle>Desktop Notifications · Adoption</CardTitle>
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={fetch}
                        disabled={loading}
                        title="Refresh"
                        data-testid="push-stats-refresh"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
                <CardDescription>
                    See which employees have desktop push enabled — useful for nudging anyone who keeps missing chat or HR pings.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {loading && !stats ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                ) : !stats ? (
                    <div className="text-sm text-muted-foreground">No data.</div>
                ) : (
                    <>
                        <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="rounded-lg border bg-muted/30 p-3" data-testid="push-stat-total">
                                <div className="text-xs text-muted-foreground">Total users</div>
                                <div className="text-2xl font-bold tabular-nums">{stats.total_users}</div>
                            </div>
                            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="push-stat-enabled">
                                <div className="text-xs text-emerald-600 flex items-center gap-1">
                                    <CheckCircle2 className="h-3 w-3" /> Enabled
                                </div>
                                <div className="text-2xl font-bold tabular-nums text-emerald-600">{stats.enabled_count}</div>
                            </div>
                            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="push-stat-disabled">
                                <div className="text-xs text-amber-600 flex items-center gap-1">
                                    <XCircle className="h-3 w-3" /> Not yet enabled
                                </div>
                                <div className="text-2xl font-bold tabular-nums text-amber-600">{stats.disabled_count}</div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 mb-3">
                            <div className="flex rounded-md border p-1 bg-muted/20">
                                <Button
                                    size="sm"
                                    variant={tab === 'disabled' ? 'default' : 'ghost'}
                                    className="h-7 px-3 text-xs"
                                    onClick={() => setTab('disabled')}
                                    data-testid="push-stats-tab-disabled"
                                >
                                    To nudge ({stats.disabled_count})
                                </Button>
                                <Button
                                    size="sm"
                                    variant={tab === 'enabled' ? 'default' : 'ghost'}
                                    className="h-7 px-3 text-xs"
                                    onClick={() => setTab('enabled')}
                                    data-testid="push-stats-tab-enabled"
                                >
                                    Enabled ({stats.enabled_count})
                                </Button>
                            </div>
                            <div className="relative flex-1 max-w-xs">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                                <Input
                                    placeholder="Filter by name"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-8 h-8 text-xs"
                                    data-testid="push-stats-search"
                                />
                            </div>
                        </div>

                        <ScrollArea className="h-48 rounded-md border bg-muted/10 p-2">
                            {filtered.length === 0 ? (
                                <div className="text-center text-xs text-muted-foreground py-6">
                                    {tab === 'enabled' ? 'Nobody has enabled yet.' : 'Everyone is enabled — nothing to nudge!'}
                                </div>
                            ) : (
                                filtered.map((u) => <UserRow key={u.id} user={u} tone={tab === 'enabled' ? 'on' : 'off'} />)
                            )}
                        </ScrollArea>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
